import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtCVE, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, HardHat, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

export const WORK_STATUS = {
  planeada: { label: "Planeada", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  em_curso: { label: "Em curso", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  suspensa: { label: "Suspensa", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  concluida: { label: "Concluída", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelada: { label: "Cancelada", cls: "bg-red-100 text-red-800 border-red-200" },
};

export default function Works() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", client_id: "", address: "", contract_value: 0, start_date: "", end_date: "", status: "planeada" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, c] = await Promise.all([api.get("/works"), api.get("/clients", { params: { limit: 200 } })]);
      setItems(w.data.items); setClients(c.data.items);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("O nome da obra é obrigatório");
    setBusy(true);
    try {
      const { data } = await api.post("/works", { ...form, contract_value: Number(form.contract_value),
        client_id: form.client_id || null, start_date: form.start_date || null, end_date: form.end_date || null });
      toast.success("Obra criada");
      setOpen(false); navigate(`/obras/${data.id}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="works-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Projetos</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Obras</h1>
        </div>
        <Button className="rounded-full" onClick={() => { setForm({ name: "", client_id: "", address: "", contract_value: 0, start_date: "", end_date: "", status: "planeada" }); setOpen(true); }} data-testid="new-work-btn">
          <Plus size={18} className="mr-1.5" /> Nova Obra
        </Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><CircleNotch size={24} className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-border bg-card py-16 text-center">
          <HardHat size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground">Ainda não há obras.</p>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)}>Criar primeira obra</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((w) => {
            const st = WORK_STATUS[w.status] || WORK_STATUS.planeada;
            return (
              <button key={w.id} onClick={() => navigate(`/obras/${w.id}`)} data-testid={`work-card-${w.id}`}
                className="stagger-item flex flex-col rounded-xl border border-border bg-card p-5 text-left transition-shadow hover:shadow-md">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-mono text-[11px] text-muted-foreground">{w.code}</div>
                    <h3 className="font-display text-lg font-bold leading-tight">{w.name}</h3>
                  </div>
                  <Badge variant="outline" className={st.cls}>{st.label}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{w.client_name || "Sem cliente"}</p>
                <div className="mt-4">
                  <div className="mb-1 flex justify-between text-xs text-muted-foreground">
                    <span>Conclusão</span><span>{Math.round(w.completion || 0)}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, w.completion || 0)}%` }} />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{fmtDate(w.start_date)}</span>
                  <span className="font-semibold">{fmtCVE(w.contract_value)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="work-dialog">
          <DialogHeader><DialogTitle>Nova obra</DialogTitle>
            <DialogDescription>Depois de criar, poderá adicionar equipa, tarefas, materiais, fotos e relatórios.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Nome da obra *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="work-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="work-client"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valor contratado (CVE)</Label>
              <Input type="number" value={form.contract_value} onChange={(e) => setForm({ ...form, contract_value: e.target.value })} data-testid="work-value" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="work-address" />
            </div>
            <div className="space-y-1.5">
              <Label>Início</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="work-start" />
            </div>
            <div className="space-y-1.5">
              <Label>Fim previsto</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="work-end" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={busy} data-testid="save-work-btn">
              {busy ? <CircleNotch className="animate-spin" /> : "Criar obra"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
